import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type CardSpec = {
  id: 1 | 2 | 3 | 4;
  img: string;
  x: number;
  y: number;
  w: number;
  z: number;
};

const cards: CardSpec[] = [
  {
    id: 1,
    img: "/assets/images/serviceIntro/card1_1.png",
    x: 70,
    y: 270,
    w: 306,
    z: 20,
  },
  {
    id: 2,
    img: "/assets/images/serviceIntro/card1_2.png",
    x: 145,
    y: 170,
    w: 186,
    z: 30,
  },
  {
    id: 3,
    img: "/assets/images/serviceIntro/card1_3.png",
    x: 370,
    y: 130,
    w: 186,
    z: 40,
  },
  {
    id: 4,
    img: "/assets/images/serviceIntro/card1_4.png",
    x: 150,
    y: 450,
    w: 403,
    z: 10,
  },
];

const SHADOW = "drop-shadow(0 18px 38px rgba(0,0,0,0.12))";
const START_FILTER = `blur(12px) saturate(0.95) brightness(1.02) ${SHADOW}`;
const END_FILTER = `blur(0px) saturate(1) brightness(1) ${SHADOW}`;

export default function ServiceIntroCardSequence() {
  const BASE_W = 623;
  const BASE_H = 604;

  const outerRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const ranRef = useRef(false);

  const [scope, animate] = useAnimate();
  const [outerWidth, setOuterWidth] = useState(BASE_W);

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? BASE_W;
      setOuterWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = Math.min(outerWidth / BASE_W, 1);
  const scaledH = BASE_H * scale;

  useEffect(() => {
    let alive = true;
    const el = hostRef.current;
    if (!el) return;

    const run = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      for (let i = 1; i <= 4; i++) {
        if (!alive) return;

        await animate(
          `.c1-${i}`,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: END_FILTER, //  선명하게
          },
          {
            duration: 0.6,
            delay: i === 1 ? 0 : 0.12,
            ease: [0.22, 1, 0.36, 1],
          },
        );
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) run();
      },
      {
        threshold: 0,
        rootMargin: "-50% 0px -50% 0px",
      },
    );

    io.observe(el);

    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate]);

  return (
    <section className="w-full flex items-start">
      <div
        ref={outerRef}
        className="w-full max-w-[623px] bg-[#F2F4F8] rounded-[24px] overflow-hidden relative"
        style={{
          height: scaledH,
          boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
        }}
      >
        <img
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        <div
          ref={(node) => {
            hostRef.current = node;
            (scope as any).current = node;
          }}
          style={{
            width: BASE_W,
            height: BASE_H,
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {cards.map((c) => (
            <motion.img
              key={c.id}
              className={`c1-${c.id}`}
              src={c.img}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: c.x,
                top: c.y,
                width: c.w,
                zIndex: c.z,
                userSelect: "none",

                //  처음부터 "살짝 보이되 흐릿한" 상태
                opacity: 0.6,
                transform: "translateY(14px) scale(0.985)",
                filter: START_FILTER,

                //  filter 애니메이션 깜빡임 방지 (GPU 힌트)
                willChange: "transform, opacity, filter",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
